import { asArray, asId, asNumber, asObject, asText } from "../coerce";
import type {
  EvaluationApiItem,
  EvaluationData,
  EvaluationQuestion,
} from "../../../@types/portalAluno";

const TYPES_WITH_SCORE = [
  "T1",
  "T5",
  "Avaliação de Conhecimento - Final",
  "Avaliação de Conhecimento - Inicial",
  "Quiz",
];

const orderByPosition = (items: EvaluationApiItem[]) =>
  [...items].sort(
    (first, second) =>
      (asNumber(first.nrOrdem) ?? Number.MAX_SAFE_INTEGER) -
      (asNumber(second.nrOrdem) ?? Number.MAX_SAFE_INTEGER)
  );

const shuffleItems = <T,>(items: T[]) => {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }
  return shuffled;
};

const mapQuestion = (value: unknown): EvaluationQuestion | undefined => {
  const raw = asObject<EvaluationApiItem>(value);
  if (!raw) return undefined;
  const id = asId(raw.idAvaliacaoPergunta);
  const title = asText(raw.nmAvaliacaoPergunta);
  if (!id || !title) return undefined;

  const isText = Number(raw.icPerguntaDissertativa) === 1;
  const options = isText
    ? undefined
    : orderByPosition(
        asArray<EvaluationApiItem>(raw.avaliacaoPerguntaRespostas)
      ).flatMap((option) => {
        const optionId = asId(option.idAvaliacaoPerguntaResposta);
        const label = asText(option.dsAvaliacaoPerguntaResposta);
        if (!optionId || !label) return [];

        return [
          {
            id: String(optionId),
            isCorrect:
              option.icCorreta === true || Number(option.icCorreta) === 1,
            label,
          },
        ];
      });

  return {
    description: asText(raw.dsAvaliacaoPergunta),
    feedback: asText(raw.feedbackPergunta) ?? "Resposta registrada.",
    id: String(id),
    mediaUrl: asText(raw.pathMidia),
    options,
    title,
    type: isText ? "text" : "single-choice",
  };
};

const isFeedbackReleasePending = (type: string, endDateText?: string) => {
  if (type !== "T5" || !endDateText) return false;
  const endDate = new Date(endDateText);

  return !Number.isNaN(endDate.getTime()) && endDate.getTime() > Date.now();
};

export const mapEvaluation = (value: unknown): EvaluationData => {
  const raw = asObject<EvaluationApiItem>(value);
  if (!raw) throw new Error("A API retornou uma avaliação inválida.");
  const id = asId(raw.idAvaliacao);
  const title = asText(raw.nmAvaliacao);
  if (!id || !title) {
    throw new Error("A avaliação solicitada não foi encontrada.");
  }

  const mappedQuestions = orderByPosition(
    asArray<EvaluationApiItem>(raw.avaliacaoPerguntas)
  )
    .map(mapQuestion)
    .filter((question): question is EvaluationQuestion => Boolean(question));
  const questions =
    Number(raw.icAleatorio) === 1
      ? shuffleItems(mappedQuestions)
      : mappedQuestions;

  const type =
    asText(asObject<EvaluationApiItem>(raw.avaliacaoTipo)?.nmAvaliacaoTipo) ??
    asText(raw.nmAvaliacaoTipo) ??
    "Avaliação";

  return {
    description:
      asText(raw.dsAvaliacao) ?? "Responda todas as perguntas para concluir.",
    estimatedTime: `${Math.max(1, questions.length * 2)} min`,
    id,
    questions,
    showFeedback: !isFeedbackReleasePending(type, asText(raw.dtAvaliacaoFim)),
    showScore: TYPES_WITH_SCORE.includes(type),
    title,
    type,
  };
};

export const buildEvaluationAnswersPayload = (
  questions: EvaluationQuestion[],
  answers: Record<string, string | undefined>
) =>
  questions.map((question) => {
    const selectedAnswer = answers[question.id]?.trim() ?? "";
    const selectedOption = question.options?.find(
      (option) => option.id === selectedAnswer
    );
    const questionId = Number(question.id);

    return {
      avaliacaoPergunta: { idAvaliacaoPergunta: questionId },
      avaliacaoPerguntaResposta: selectedOption
        ? {
            avaliacaoPergunta: { idAvaliacaoPergunta: questionId },
            idAvaliacaoPerguntaResposta: Number(selectedOption.id),
          }
        : null,
      dsAvaliacaoPessoaResposta:
        question.type === "text" ? selectedAnswer : null,
      icCorreta: selectedOption ? (selectedOption.isCorrect ? 1 : 0) : null,
    };
  });
